/**
 * LotSmith — synthetic 30-day equity + trade history.
 *
 * Used by DevProvider until real telemetry exists. Deterministic so the
 * dashboard renders identically across page reloads — important for the
 * Playwright visual-regression target we'll add in a later milestone.
 */

export interface EquityPoint {
  /** ISO-8601 UTC. */
  ts: string;
  equity: number;
  /** Closed P&L for the day (only on day-end points). */
  dailyPnl?: number;
}

export type Mood = "calm" | "confident" | "anxious" | "uncertain" | "revenge";

export interface DemoTrade {
  id: string;
  ts: string;
  symbol: string;
  direction: "LONG" | "SHORT";
  volume: number;
  pnl: number;
  note?: string | null;
  mood?: Mood | null;
  setupTags?: string[] | null;
  mistakeTags?: string[] | null;
}

const SYMBOLS = ["EURUSD", "XAUUSD", "USDJPY", "BTCUSD", "NAS100", "GBPUSD", "EURJPY"];
const START_BALANCE = 100_000;
const TARGET_PNL_PCT = 0.024; // ~2.4 % over 30 days

/** Seeded LCG so the timeline is identical every reload. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export interface DemoData {
  equity: EquityPoint[];
  trades: DemoTrade[];
}

export function buildDemoData(): DemoData {
  const equity: EquityPoint[] = [];
  const trades: DemoTrade[] = [];

  const r = rng(0xa1b2c3d4);
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const start = now - 30 * dayMs;

  let eq = START_BALANCE;

  // 30 days * 24 hourly samples = 720 equity ticks. Trades happen on the
  // hourly cadence with ~30 % probability.
  for (let h = 0; h < 30 * 24; h++) {
    const ts = new Date(start + h * 3600 * 1000);
    const isClose = h > 0 && r() < 0.32;

    if (isClose) {
      const sym = SYMBOLS[Math.floor(r() * SYMBOLS.length)]!;
      const dir: "LONG" | "SHORT" = r() < 0.55 ? "LONG" : "SHORT";
      // Bias toward small wins, occasional outsized losses — realistic.
      const winBias = r() < 0.62;
      const magnitude = 30 + r() * 240;
      const pnl = (winBias ? magnitude : -magnitude * (1 + r() * 0.5)) *
                  (sym === "BTCUSD" ? 4 : 1);
      eq += pnl;
      // Drift toward TARGET_PNL_PCT so the curve trends upward overall.
      const targetEq = START_BALANCE * (1 + TARGET_PNL_PCT * (h / (30 * 24)));
      const drift = (targetEq - eq) * 0.01;
      eq += drift;

      const volume = sym === "BTCUSD" ? 0.05 : sym === "XAUUSD" ? 0.1 : 0.5;
      trades.push({
        id: `t-${h}`,
        ts: ts.toISOString(),
        symbol: sym,
        direction: dir,
        volume,
        pnl: Math.round(pnl * 100) / 100,
      });
    }

    equity.push({
      ts: ts.toISOString(),
      equity: Math.round(eq * 100) / 100,
    });
  }

  return { equity, trades: trades.reverse() };
}
